// src/helpers/api/panel.ts

import type { ApiPanelListItem } from "@/helpers/interfaces/panel";
import type {
  PanelConfiguration,
  SimplifiedDiscordChannel,
} from "@/helpers/interfaces/panelSchema";
import { apiFetch } from "../http-client";
import { handleAPIResponse } from "./errorHandler";
import { handleBotKickedResponse } from "./botKickedHandler";
import { dedupedFetch, clearDedupedRequest } from "../requestDeduplicator";
import { getCachedData, invalidateCache } from "../apiCache";

const VITE_API_URL = import.meta.env.VITE_API_URL || "";

// Standardized error wrapper to preserve HTTP status codes
const createApiError = (message: string, status: number, code?: string) => {
  const error: any = new Error(message);
  error.status = status;
  error.code = code;
  return error;
};

// Interface for the simplified role data expected from the backend /api/guilds/:guildId/roles endpoint
export interface BackendDiscordRole {
  id: string;
  name: string;
  color: number;
  locked?: boolean;
  reason?: string;
}

export type ReportReason =
  | "profanity"
  | "hate_speech"
  | "spam_or_misleading"
  | "malicious_content"
  | "other";

// --- Panel List & CRUD Functions ---

export const fetchPanelsForServer = async (
  guildId: string,
  force = false
): Promise<ApiPanelListItem[]> => {
  const cacheKey = `panels:${guildId}`;

  if (force) {
    invalidateCache(cacheKey);
    clearDedupedRequest(cacheKey);
  }

  return dedupedFetch(cacheKey, () =>
    getCachedData(cacheKey, () => fetchPanelsDirectly(guildId, force))
  );
};

async function fetchPanelsDirectly(guildId: string, force = false): Promise<ApiPanelListItem[]> {
  const apiUrl = `${VITE_API_URL}/api/panel/guild/${guildId}${force ? "?force=true" : ""}`;

  const response = await apiFetch(apiUrl, {
    method: "GET",
    credentials: "include",
  });

  let data: any;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) throw createApiError(`HTTP Error ${response.status}`, response.status);
    throw new Error("Failed to parse response");
  }

  if (!response.ok) {
    throw createApiError(data?.error || data?.message || "Failed to fetch panels", response.status);
  }

  if (!Array.isArray(data)) {
    throw new Error("Received invalid panel data format from the server.");
  }
  return data;
}

// --- Updated Interface for Payload ---
export interface PanelConfigOverride {
  managementRoleIds?: string[];
  category?: {
    openedTicketsCategoryId?: string;
  };
  archive?: {
    logChannelId?: string;
  };
}

export interface CreatePanelFromTemplatePayload {
  guildId: string;
  panelTitle: string;
  panelOverrides?: Record<string, any>;
  language?: string;
  excludedPanelIds?: string[]; // <-- ADD THIS
}

export async function createPanelFromTemplate(
  templateId: string,
  payload: CreatePanelFromTemplatePayload
): Promise<any> {
  const apiUrl = `${VITE_API_URL}/api/templates/${templateId}/apply`;
  try {
    const response = await apiFetch(apiUrl, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();
    if (!response.ok) {
      throw createApiError(responseData.error || responseData.message || "Failed to create panel", response.status);
    }
    if (!responseData || !responseData.panel) {
      throw new Error("Panel created, but received invalid data.");
    }

    if (payload.guildId) invalidateCache(`panels:${payload.guildId}`);
    return responseData.panel;
  } catch (err: any) {
    throw err;
  }
}

export const deletePanel = async (guildId: string, panelId: string): Promise<void> => {
  if (!guildId || !panelId) throw new Error("Required IDs missing for panel deletion.");
  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${panelId}`;
  const response = await apiFetch(apiUrl, { method: "DELETE", credentials: "include" });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw createApiError(data.error || data.message || "Failed to delete panel", response.status);
  }
  invalidateCache(`panels:${guildId}`);
};

export async function revertPanelToFree(guildId: string, panelId: string): Promise<void> {
  if (!guildId || !panelId) throw new Error("Missing guildId or panelId for reversion.");
  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${panelId}/revert`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw createApiError(data.error || data.message || "Failed to revert panel", response.status);
  }
  invalidateCache(`panels:${guildId}`);
}

// --- Panel Configuration Functions ---

export async function getPanelDetails(guildId: string, panelId: string): Promise<PanelConfiguration> {
  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${panelId}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });

  let data: any;
  try {
    data = await response.json();
  } catch {
    throw createApiError(`HTTP Error ${response.status}`, response.status);
  }

  if (!response.ok) {
    throw createApiError(data?.error || data?.message || "Failed to fetch panel details", response.status);
  }
  return data as PanelConfiguration;
}

export async function updatePanelDetails(
  guildId: string,
  panelId: string,
  panelData: Partial<PanelConfiguration>
): Promise<PanelConfiguration> {
  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${panelId}`;
  const response = await apiFetch(apiUrl, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(panelData),
  });

  const data = await response.json();
  if (!response.ok) {
    throw createApiError(data.error || data.message || "Failed to save panel", response.status);
  }
  if (!data || !data.panel) {
    throw new Error("Panel data not found in the server's save response.");
  }

  invalidateCache(`panels:${guildId}`);
  return data.panel as PanelConfiguration;
}

// --- Guild Data Functions ---

export const getGuildRoles = async (serverId: string, forceRefresh = false): Promise<BackendDiscordRole[]> => {
  const url = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId}/roles${forceRefresh ? '?force=true' : ''}`;
  const response = await apiFetch(url, { method: "GET", credentials: "include" });

  let data: any;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) throw createApiError(`HTTP Error ${response.status}`, response.status);
    throw new Error("Failed to parse roles");
  }

  if (!response.ok) {
    // Correctly preserves the 425 status code and JSON error body for the frontend catch block
    throw createApiError(data?.error || data?.message || "Failed to fetch roles", response.status);
  }
  return data;
};

export const getGuildChannels = async (serverId: string, forceRefresh = false): Promise<SimplifiedDiscordChannel[]> => {
  const url = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId}/channels${forceRefresh ? '?force=true' : ''}`;
  const response = await apiFetch(url, { method: "GET", credentials: "include" });

  let data: any;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) throw createApiError(`HTTP Error ${response.status}`, response.status);
    throw new Error("Failed to parse channels");
  }

  if (!response.ok) {
    throw createApiError(data?.error || data?.message || "Failed to fetch channels", response.status);
  }
  return data;
};

// --- Template Functions ---

export interface TemplateForFrontend {
  id: string;
  panelId?: string;
  name: string;
  description: string;
  isPremium: boolean;
  icon: string;
  category: "free" | "premium";
  config: Partial<PanelConfiguration>;
  connectedConfigs?: Partial<PanelConfiguration>[];
  _id?: string;
  usageCount?: number;
  isOfficial?: boolean;
  tags?: string[];
  createdAt?: string;
  translations?: Record<string, any>;
}

export async function fetchTemplatesByFilters(
  tier: "all" | "free" | "premium",
  isOfficial: boolean,
  queryParams: Record<string, string | number> = {}
): Promise<{ templates: TemplateForFrontend[]; hasMore: boolean; total: number; }> {
  const officialSegment = isOfficial ? "official" : "unofficial";
  let url = `${VITE_API_URL}/api/templates/${tier}/${officialSegment}`;

  const params = new URLSearchParams({ sortBy: "usageCount", limit: "10" });
  for (const key in queryParams) {
    params.append(key, String(queryParams[key]));
  }
  url += `?${params.toString()}`;

  const response = await apiFetch(url, { method: "GET", credentials: "include" });
  let data: any;
  try {
    data = await response.json();
  } catch {
    throw createApiError(`HTTP Error ${response.status}`, response.status);
  }

  if (!response.ok) throw createApiError(data?.error || data?.message || "Failed to fetch templates", response.status);
  if (!data || !Array.isArray(data.templates)) throw new Error(`Received invalid template data format.`);

  const templates = data.templates.map((template: any): TemplateForFrontend => ({
    id: template._id,
    panelId: template.panelId,
    name: template.title,
    description: template.description,
    isPremium: template.mainPanelConfig?.isPremium || false,
    icon: "FilePlus",
    category: template.mainPanelConfig?.isPremium ? "premium" : "free",
    config: template.mainPanelConfig || {},
    connectedConfigs: template.connectedPanelConfigs || [],
    usageCount: template.usageCount,
    isOfficial: template.isOfficial,
    tags: template.tags || [],
    createdAt: template.createdAt,
    translations: template.translations || {},
  }));

  return {
    templates,
    hasMore: data.hasMore || false,
    total: data.total || templates.length,
  };
}

export async function fetchTemplateDetails(templateId: string): Promise<TemplateForFrontend> {
  if (!templateId) throw new Error("A template ID is required to fetch details.");
  const url = `${VITE_API_URL}/api/templates/details/${templateId}`;

  const response = await apiFetch(url, { method: "GET", credentials: "include" });
  let template: any;
  try {
    template = await response.json();
  } catch {
    throw createApiError(`HTTP Error ${response.status}`, response.status);
  }

  if (!response.ok) throw createApiError(template?.error || template?.message || "Failed to fetch template", response.status);

  return {
    id: template._id,
    panelId: template.panelId,
    name: template.title,
    description: template.description,
    isPremium: template.mainPanelConfig?.isPremium || false,
    icon: "FilePlus",
    category: template.mainPanelConfig?.isPremium ? "premium" : "free",
    config: template.mainPanelConfig,
    connectedConfigs: template.connectedPanelConfigs,
    usageCount: template.usageCount,
    isOfficial: template.isOfficial,
    tags: template.tags || [],
    createdAt: template.createdAt,
    translations: template.translations || {},
  };
}

export interface CreateTemplateFromPanelPayload {
  sourceCustomPanelId: string;
  sourceGuildId: string;
  templateTitle: string;
  templateDescription: string;
  makeGlobal?: boolean;
  tags?: string[];
}

export async function createTemplateFromPanel(payload: CreateTemplateFromPanelPayload): Promise<any> {
  const apiUrl = `${VITE_API_URL}/api/templates/from-panel`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });

  const responseData = await response.json();
  if (!response.ok) throw createApiError(responseData.error || responseData.message || "Failed to create template", response.status);
  return responseData.template;
}

export interface CheckTemplateTierResponse {
  tier: "free" | "premium";
  reasons?: string[];
  reason?: string | null;
  details?: any;
}

export async function checkTemplateTier(guildId: string, panelId: string): Promise<CheckTemplateTierResponse> {
  if (!guildId || !panelId) throw new Error("Guild ID and Panel ID are required.");
  const apiUrl = `${VITE_API_URL}/api/templates/check-tier`;

  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ sourceGuildId: guildId, sourceCustomPanelId: panelId }),
  });

  const responseData = await response.json();
  if (!response.ok) throw createApiError(responseData.error || responseData.message || "Failed to check tier", response.status);
  return responseData as CheckTemplateTierResponse;
}

// --- History Functions ---
export interface IChange { field: string; oldValue: any; newValue: any; }
export interface IHistoryUser { id: string; username: string; }
export interface IPanelHistoryEntry {
  _id: string; panelId: string; guildId: string; action: string;
  panelName: string; user: IHistoryUser; changes: IChange[]; createdAt: string;
}
export interface IHistoryResponse {
  history: IPanelHistoryEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number; };
}
export interface IHistoryFilters {
  userId?: string; 
  username?: string; 
  panelId?: string; 
  action?: string;
  targetType?: string;
  sortBy?: "createdAt"; 
  sortOrder?: "asc" | "desc";
}

export async function fetchPanelHistory(
  guildId: string, panelId: string, page = 1, limit = 10, filters: IHistoryFilters = {}
): Promise<IHistoryResponse> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters.userId) params.append("userId", filters.userId);
  if (filters.username) params.append("username", filters.username);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${panelId}/history?${params.toString()}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to fetch history", response.status);
  return data as IHistoryResponse;
}

export async function fetchGuildHistory(
  guildId: string, page = 1, limit = 20, filters: IHistoryFilters = {}
): Promise<IHistoryResponse> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters.action) params.append("action", filters.action);
  if (filters.panelId) params.append("panelId", filters.panelId);
  if (filters.userId) params.append("userId", filters.userId);
  if (filters.username) params.append("username", filters.username);
  if (filters.targetType) params.append("targetType", filters.targetType);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

  const apiUrl = `${VITE_API_URL}/api/panel/guild/${guildId}/history?${params.toString()}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to fetch guild history", response.status);
  return data as IHistoryResponse;
}

export async function clonePanel(guildId: string, sourcePanelId: string, newPanelName: string): Promise<PanelConfiguration> {
  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${sourcePanelId}/clone`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ newPanelName }),
  });

  const responseData = await response.json();
  if (!response.ok) throw createApiError(responseData.error || responseData.message || "Failed to clone panel", response.status);
  invalidateCache(`panels:${guildId}`);
  return responseData.panel as PanelConfiguration;
}

export async function createPanelShareCode(guildId: string, panelId: string): Promise<{ code: string; expiresAt: string; replacedExisting: boolean }> {
  const apiUrl = `${VITE_API_URL}/api/panel/${guildId}/${panelId}/share`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });

  const responseData = await response.json();
  if (!response.ok) throw createApiError(responseData.error || responseData.message || "Failed to create share code", response.status);
  return responseData;
}

export interface CreateFromCodePayload {
  shareCode: string; guildId: string; panelTitle: string;
  supportRoleIds?: string[]; ticketCategoryId?: string; archiveChannelId?: string;
  panelOverrides?: Record<string, any>; excludedPanelIds?: string[];
}

export async function createPanelFromShareCode(payload: CreateFromCodePayload): Promise<ApiPanelListItem> {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/panel/share/apply`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      shareCode: payload.shareCode, guildId: payload.guildId, newPanelName: payload.panelTitle,
      supportRoleIds: payload.supportRoleIds, ticketCategoryId: payload.ticketCategoryId, archiveChannelId: payload.archiveChannelId,
      panelOverrides: payload.panelOverrides, excludedPanelIds: payload.excludedPanelIds,
    }),
  });

  const responseData = await response.json();
  if (!response.ok) throw createApiError(responseData.error || responseData.message || "Failed to apply share code", response.status);

  invalidateCache(`panels:${payload.guildId}`);
  responseData.panel.panelTitleText = responseData.panel.panelName;
  return responseData.panel as ApiPanelListItem;
}

export const reportTemplate = async (templateId: string, reason: ReportReason, additionalInfo?: string): Promise<{ message: string }> => {
  const apiUrl = `${VITE_API_URL}/api/templates/${templateId}/report`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ reason, additionalInfo }),
  });

  const responseData = await response.json();
  if (!response.ok) throw createApiError(responseData.error || responseData.message || "Failed to report template", response.status);
  return responseData;
};

export async function fetchTemplatesForReview(params: { search?: string; highPriorityPage?: number; standardPriorityPage?: number; }): Promise<any> {
  const query = new URLSearchParams();
  if (params.search) query.append("search", params.search);
  if (params.highPriorityPage) query.append("highPriorityPage", String(params.highPriorityPage));
  if (params.standardPriorityPage) query.append("standardPriorityPage", String(params.standardPriorityPage));

  const apiUrl = `${VITE_API_URL}/api/moderation/review?${query.toString()}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to fetch review queue", response.status);
  return data;
}

export async function updateTemplateStatus(templateId: string, action: "approve" | "reject"): Promise<{ message: string }> {
  const apiUrl = `${VITE_API_URL}/api/moderation/review/${templateId}`;
  const response = await apiFetch(apiUrl, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to update template status", response.status);
  return data;
}

export async function fetchModerationLogs(page = 1, limit = 20, filters: any = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  for (const key in filters) if (filters[key]) params.append(key, filters[key]);

  const apiUrl = `${VITE_API_URL}/api/moderation/logs?${params.toString()}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to fetch logs", response.status);
  return data;
}

export async function fetchAdminTemplates(filters: any = {}, page = 1, limit = 15) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  for (const key in filters) if (filters[key]) params.append(key, filters[key]);

  const apiUrl = `${VITE_API_URL}/api/moderation/templates/manage?${params.toString()}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to fetch templates", response.status);
  return data;
}

export async function updateAdminTemplate(templateId: string, payload: { title?: string; description?: string; tags?: string[] }): Promise<any> {
  const apiUrl = `${VITE_API_URL}/api/moderation/templates/manage/${templateId}`;
  const response = await apiFetch(apiUrl, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to update template", response.status);
  return data;
}

export async function deleteAdminTemplate(templateId: string): Promise<{ message: string }> {
  const apiUrl = `${VITE_API_URL}/api/moderation/templates/manage/${templateId}`;
  const response = await apiFetch(apiUrl, { method: "DELETE", credentials: "include" });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to delete template", response.status);
  return data;
}

export async function updateAdminTemplateConfig(templateId: string, payload: { mainPanelConfig: Partial<PanelConfiguration>; connectedPanelConfigs?: Partial<PanelConfiguration>[]; }): Promise<any> {
  const apiUrl = `${VITE_API_URL}/api/moderation/templates/manage/config/${templateId}`;
  const response = await apiFetch(apiUrl, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to update config", response.status);
  return data;
}

export const sendPanel = async (guildId: string, panelId: string, channelId: string): Promise<any> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/panel/${guildId}/${panelId}/send`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ channelId }),
    allow404: true
  } as any);
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to send panel", response.status);
  return data;
};

export const updatePanel = async (guildId: string, panelId: string, messageLink: string): Promise<any> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/panel/${guildId}/${panelId}/update`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messageLink }),
    allow404: true
  } as any);
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to update panel", response.status);
  return data;
};

export interface ShareCodeInfo { found: boolean; isPremium: boolean; panelName: string; }

export async function inspectShareCode(code: string): Promise<ShareCodeInfo> {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/panel/share/${code}`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw createApiError(data.error || data.message || "Invalid share code", response.status);
  return data;
}